/** @graphql cc **/
import gql from 'graphql-tag';

import { query } from '@confluence/query-preloader-tools/entry-points/graphql';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

import type {
	ConfluenceSpaceRoleMode,
	preloadSpaceRoleModeQuery as preloadSpaceRoleModeQueryType,
} from './__types__/preloadSpaceRoleModeQuery';

const preloadSpaceRoleModeQuery = gql`
	query preloadSpaceRoleModeQuery($cloudId: ID!) {
		confluence_spaceRoleMode(cloudId: $cloudId)
	}
`;

/**
 * Creates a Promise that resolves when the space role mode query completes.
 *
 * @param cloudId - The cloud ID to query for
 * @returns A Promise that resolves with the query data or rejects with an error
 */
export const createSpaceRoleModeQueryPromise = (
	cloudId: string,
): Promise<ConfluenceSpaceRoleMode | null> => {
	return query<preloadSpaceRoleModeQueryType, { cloudId: string }>({
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		query: preloadSpaceRoleModeQuery,
		variables: { cloudId },
		fetchPolicy: 'cache-first',
	})
		.then(({ data, hasErrors }) => {
			if (hasErrors) {
				// Handle errors silently for preloading
				return null;
			}
			return data?.confluence_spaceRoleMode || null;
		})
		.catch((error) => {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PERMISSIONS_EXPERIENCE,
			});
			markErrorAsHandled(error);
			return null;
		});
};

/**
 * Preloads the space role mode query to prevent UI flickers.
 * This should be called alongside any blocked access validation checks.
 */
export const preloadSpaceRoleMode = async (cloudId: string): Promise<void> => {
	await createSpaceRoleModeQueryPromise(cloudId);
};
